<template lang="pug">
  #teams-page
    WtTile(v-if="!isTeamsFeatureOn")
      section.section.title Oops, the teams feature is turned off.  Please contact your administrator.
    WtTile(ancestor vertical v-else)
      WtTile(parent)
        WtTile(child child-class="")
          .level.teams-header.is-mobile
            .level-left
              .level-item
                WtTitle(h1) Teams
            .level-right
              .level-item(v-if="programLoaded && (coreProgramMeta.memberCanManageTeams || $auth.user().isAdmin)")
                BTooltip(
                  :active="disabledCreateTeamButton"
                  label="You can only join one team at a time. To create a new team, leave your existing team."
                  position="is-left"
                  animated
                  multilined
                )
                  WtButton(
                    semibold
                    :disabled="disabledCreateTeamButton"
                    :color="disabledCreateTeamButton ? 'lightgrey' : 'primary'"
                    :isRouterLink="!disabledCreateTeamButton"
                    :to="{ name: 'MemberTeamBuilder', params: { mode: isAdmin ? 'admin' : 'member' } }"
                  ) {{ isMobile ? "CREATE NEW" : "CREATE A NEW TEAM" }}
      WtTile
        WtTile(vertical parent :col="8")
          WtTile(child :col="12" :is-loading="!memberTeamsLoaded")
            MemberTeamsTable
          WtTile(child :col="12" :is-loading="!programTeamsLoaded || isSearching")
            ProgramTeamsTable(
              :teams-response-object="localTeamsResponseObject"
              @search="onSearch($event)"
              :fetch-action="getProgramTeams"
            )
        WtTile(vertical parent :col="4")
          WtTile(child :col="12" v-if="_.has(featuredChallenge, 'between') && featuredChallenge.between === 'groups'")
            FeaturedChallengeSmall(:featured-challenge="featuredChallenge")
          WtTile.is-sticky-on-scroll(child :col="12" :is-loading="!challengesLoaded")
            WtTitle(card-title v-if="!challengesLoaded") My Current Team Challenges
            WtEntityList(
              v-if="challengesLoaded"
              :data="teamChallenges"
              :no-items-text="{ isActive: challengesLoaded && _.isEmpty(teamChallenges), text: '*You are not in any current team challenges.*' }"
              :footer="{ display: true, text: 'VIEW ALL CHALLENGES' }"
              title="My Current Team Challenges"
              to="Challenges"
              entityType="team"
            )
    </template>

<script>
import { mapActions, mapGetters } from "vuex";
import EventBus from "@/eventBus";
import WtButton from "@@/WtButton/WtButton";
import WtTile from "@@/WtTile/WtTile";
import WtTitle from "@@/WtTitle/WtTitle";
import MemberTeamsTable from "@@/Teams/MemberTeamsTable";
import ProgramTeamsTable from "@@/Teams/ProgramTeamsTable";
const FeaturedChallengeSmall = () =>
  import(
    /* webpackChunkName: "FeaturedChallengeSmall" */ "@@/Challenges/FeaturedChallengeSmall"
  ).catch(() => {
    window.location.reload();
  });
import WtEntityList from "@@/WtEntityList/WtEntityList";
import { fetchAllMixin } from "@/mixins/fetchAllMixin";

export default {
  name: "Teams",

  components: {
    MemberTeamsTable,
    ProgramTeamsTable,
    WtButton,
    WtTile,
    WtTitle,
    FeaturedChallengeSmall,
    WtEntityList
  },

  mixins: [fetchAllMixin],

  data() {
    return {
      isSearching: false,
      localTeamsResponseObject: {},
      perPage: 5,
      searchQuery: ""
    };
  },

  computed: {
    ...mapGetters(["isMobile"]),
    ...mapGetters("authMember", ["isAdmin"]),
    ...mapGetters("program", [
      "programLoaded",
      "coreProgramMeta",
      "challenges",
      "challengesLoaded",
      "featuredChallenge",
      "currentFeaturedChallengeLoaded",
      "programTeamsResponseObject",
      "programTeamsLoaded",
      "isTeamsFeatureOn"
    ]),
    ...mapGetters("members", ["memberTeams", "memberTeamsLoaded"]),

    teamChallenges() {
      return this.challenges.filter(challenge => {
        if (challenge.between === "groups") {
          return !this._.isEmpty(
            this._.intersectionBy(challenge.teams, this.memberTeams, "id")
          );
        }
      });
    },

    disabledCreateTeamButton() {
      return (
        !this.isAdmin &&
        !this.coreProgramMeta.memberCanJoinMultpleTeams &&
        this.memberTeams.length > 0
      );
    }
  },

  async mounted() {
    EventBus.onProgramTeamsResponse($event => {
      this.localTeamsResponseObject = $event;
    });

    //ensures the memberTeamsTable always updates to latest data
    if (this.isTeamsFeatureOn || !this.memberTeamsLoaded) {
      await this.fetchProgramTeams();
      await this.fetchMemberTeams(this.$auth.user().id);
    }

    this.onSearch = this._.debounce(async query => {
      this.searchQuery = query;
      if (query.length > 0) {
        this.isSearching = true;
        const response = await this.searchProgramTeams({
          programId: this.$auth.user().programId,
          query,
          perPage: this.perPage
        });
        this.localTeamsResponseObject = response;
        this.isSearching = false;
      } else {
        this.localTeamsResponseObject = this.programTeamsResponseObject;
      }
    }, 500);
  },

  beforeDestroy() {
    EventBus.offProgramTeamsResponse();
  },

  methods: {
    ...mapActions("pages", ["fetchAllPageTeams"]),
    ...mapActions("program", ["fetchProgramTeams", "searchProgramTeams"]),
    ...mapActions("members", ["fetchMemberTeams"]),

    async getProgramTeams(payload) {
      if (this.searchQuery) {
        this.localTeamsResponseObject = await this.searchProgramTeams({
          programId: this.$auth.user().programId,
          query: this.searchQuery,
          perPage: this.perPage,
          page: payload.page
        });
      } else {
        // If the search query is empty, fetch all program teams without search filter
        await this.fetchProgramTeams(payload);
      }
    },

    onSearch() {
      // defining this here, but is overwritten in the
      // mounted call.  Using debounce requires this
      // approach in order for it to work when
      // the component instance is available.
    }
  }
};
</script>

<style lang="stylus" scoped></style>
